import {
  Button,
  type ButtonProps,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  ModalClose,
  ModalDialog,
  type ModalProps,
} from '@mui/joy';
import { Form, Formik, type FormikConfig, type FormikProps, useFormikContext } from 'formik';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { type Obj } from '~/common/types';
import { type UseToggle } from '@/shared/hooks/UseToggle';

import minimizeTitleContext from '../minimizedWindows/MinimizeTitleContext';
import useMinimizableModal from '../minimizedWindows/useMinimizableModal';

import { CancelIcon, InlineSpinner, ValidIcon } from '../../../icons/Icons';
import FormMinimizeTitle from '../minimizedWindows/FormMinimizeTitle';
import MinimizeButton from '../minimizedWindows/MinimizeButton';

const SIZES_MAP = {
  lg: 'w-3/5',
  md: 'w-2/5',
  sm: 'w-1/5',
  xl: 'w-4/5',
};

type ActionProps = Pick<ButtonProps, 'color' | 'slotProps'> & {
  onClose?: () => void;
  submitDecorator?: React.ReactNode;
  submitText?: string;
};

export const FormModalActions: React.FC<ActionProps> = ({
  color,
  onClose,
  submitDecorator,
  submitText,
}) => {
  const { isSubmitting } = useFormikContext();
  const { t } = useTranslation();

  return (
    <>
      <Button
        color={color}
        disabled={isSubmitting}
        size="sm"
        startDecorator={isSubmitting ? <InlineSpinner /> : submitDecorator ?? <ValidIcon />}
        type="submit"
      >
        {submitText ?? t('buttons.save')}
      </Button>
      <Button color="neutral" size="sm" variant="outlined" onClick={onClose}>
        <CancelIcon className="pr-2" /> {t('buttons.cancel')}
      </Button>
    </>
  );
};

type BaseProps = Omit<ModalProps, 'onClose' | 'open'>;

type Props<TFormValues extends Obj, TToggleState> = BaseProps & {
  actions?: React.ReactNode;
  backdropBehavior?: 'closeOnSingleClick' | 'closeOnDoubleClick' | 'noCloseOnClick';
  className?: string;
  description?: string;
  formikConfig: FormikConfig<TFormValues>;
  getMinimizeTitle?: (values: TFormValues) => string | null | undefined;
  headerComponent?: React.ReactNode;
  icon?: React.ReactNode;
  minimizable?: boolean;
  minimizeIcon?: React.ReactNode;
  minimizeTitle?: string;
  onClose?: () => void;
  renderActions?: (props: {
    ctx: FormikProps<TFormValues>;
    onClose: () => void;
  }) => React.ReactNode;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  title?: string;
  toggle: UseToggle<TToggleState>;
};

const FormModal = <TFormValues extends Obj, TToggleState>({
  backdropBehavior = 'closeOnDoubleClick',
  children,
  className,
  description,
  formikConfig,
  getMinimizeTitle,
  headerComponent,
  icon,
  minimizable,
  minimizeIcon,
  minimizeTitle,
  onClose,
  renderActions,
  size = 'xl',
  title,
  toggle,
  ...rest
}: Props<TFormValues, TToggleState>) => {
  const handleClose = () => {
    onClose?.();
    toggle.hide();
  };

  const { canMinimize, minimize, modalProps, reportTitle } = useMinimizableModal({
    icon,
    minimizable,
    minimizeIcon,
    minimizeTitle,
    onClose: handleClose,
    open: toggle.value,
    title,
  });

  const onClickBackdrop = () => {
    if (backdropBehavior === 'closeOnSingleClick') {
      handleClose();
    }
  };

  const onDoubleClickBackdrop = () => {
    if (backdropBehavior === 'closeOnDoubleClick') {
      handleClose();
    }
  };

  return (
    toggle.value && (
      <Modal
        slotProps={{
          backdrop: {
            onClick: onClickBackdrop,
            onDoubleClick: onDoubleClickBackdrop,
          },
        }}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        {...rest}
        {...modalProps}
      >
        <ModalDialog
          className={classNames(
            'max-h-screen max-w-screen-2xl overflow-y-auto rounded-xl border border-gray-200 bg-white',
            SIZES_MAP[size],
            className,
          )}
        >
          <ModalClose />
          {canMinimize && <MinimizeButton className="right-9" onClick={minimize} />}
          {(!!icon || !!title) && (
            <DialogTitle>
              {icon} {title}
            </DialogTitle>
          )}
          {!!description && <p className="font-sans text-xs italic leading-5">{description}</p>}
          <minimizeTitleContext.Provider value={canMinimize ? reportTitle : null}>
            <Formik enableReinitialize {...formikConfig}>
              {(ctx) => (
                <Form>
                  {canMinimize && !!getMinimizeTitle && (
                    <FormMinimizeTitle getTitle={getMinimizeTitle} />
                  )}
                  {headerComponent}
                  <DialogContent className="overflow-x-hidden">{children}</DialogContent>
                  <DialogActions>
                    {renderActions?.({ ctx, onClose: handleClose }) ?? (
                      <FormModalActions onClose={handleClose} />
                    )}
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </minimizeTitleContext.Provider>
        </ModalDialog>
      </Modal>
    )
  );
};

export default FormModal;
